import React, { useEffect, useState, useCallback } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CProgress,
  CProgressBar,
  CSpinner,
} from "@coreui/react";
import { Link } from "react-router-dom";
import CIcon from "@coreui/icons-react";
import { cilChart, cilArrowTop, cilArrowBottom } from "@coreui/icons";
import { getDishStats } from "../../api/iikoApi";
import ErrorAlert from "../UI/Status/ErrorAlert";

const fmt = (n) => Number(n || 0).toLocaleString("ru-RU");

function DishTable({ rows, maxQty, color, showBar }) {
  if (rows.length === 0) {
    return <div className="text-body-secondary small py-2">Нет данных за период.</div>;
  }
  return (
    <CTable hover responsive small className="mb-0">
      <CTableHead>
        <CTableRow>
          <CTableHeaderCell scope="col">Блюдо</CTableHeaderCell>
          <CTableHeaderCell scope="col" className="text-end">
            Продано
          </CTableHeaderCell>
          <CTableHeaderCell scope="col" className="text-end">
            Выручка
          </CTableHeaderCell>
        </CTableRow>
      </CTableHead>
      <CTableBody>
        {rows.map((d) => (
          <CTableRow key={d.id}>
            <CTableDataCell>{d.name}</CTableDataCell>
            <CTableDataCell className="text-end" style={{ minWidth: 110 }}>
              {showBar && maxQty > 0 ? (
                <div className="d-flex align-items-center justify-content-end gap-2">
                  <CProgress thin color={color} className="flex-grow-1" style={{ minWidth: 40 }}>
                    <CProgressBar value={(100 * d.qty) / maxQty} />
                  </CProgress>
                  <span>{fmt(d.qty)}</span>
                </div>
              ) : (
                <span>{fmt(d.qty)}</span>
              )}
            </CTableDataCell>
            <CTableDataCell className="text-end">{fmt(d.revenue)} ₽</CTableDataCell>
          </CTableRow>
        ))}
      </CTableBody>
    </CTable>
  );
}

export default function DishStatsPanel({
  serverId,
  dateFrom,
  dateTo,
  groupByRestaurant = false,
  restaurantGroup = "",
}) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(() => {
    if (!serverId || !dateFrom || !dateTo) return;
    setLoading(true);
    setError(null);
    getDishStats(serverId, dateFrom, dateTo, groupByRestaurant, restaurantGroup)
      .then((data) => setStats(data))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [serverId, dateFrom, dateTo, groupByRestaurant, restaurantGroup]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (!serverId) return null;

  const topSellers = stats?.top_sellers ?? [];
  const leastPopular = stats?.least_popular ?? [];
  const maxTopQty = topSellers.reduce((m, d) => Math.max(m, d.qty), 0);
  const maxBottomQty = leastPopular.reduce((m, d) => Math.max(m, d.qty), 1);

  return (
    <CCard className="mb-4">
      <CCardHeader className="d-flex align-items-center gap-2 py-2">
        <CIcon icon={cilChart} />
        <span className="fw-semibold">Топ продаж по блюдам</span>
        {loading && <CSpinner size="sm" className="ms-2" />}
        {stats && (
          <span className="text-body-secondary small ms-auto d-none d-md-inline">
            {stats.sold_count} из {stats.active_menu_count} позиций меню продавались за период
          </span>
        )}
        <Link to="/dish-stats" className="btn btn-sm btn-outline-primary ms-auto ms-md-0">
          Подробнее
        </Link>
      </CCardHeader>
      <CCardBody className="pt-3">
        <ErrorAlert error={error} title="Не удалось загрузить статистику по блюдам" />
        <div className="small text-body-secondary mb-3">
          Учитываются только позиции, находящиеся в продаже сейчас — снятые с продажи блюда
          в список не попадают.
        </div>
        <CRow>
          <CCol xs={12} lg={6} className="mb-3 mb-lg-0">
            <div className="d-flex align-items-center gap-1 mb-2 text-success">
              <CIcon icon={cilArrowTop} size="sm" />
              <span className="fw-semibold small text-uppercase">Самые продаваемые</span>
            </div>
            <DishTable rows={topSellers} maxQty={maxTopQty} color="success" showBar />
          </CCol>
          <CCol xs={12} lg={6}>
            <div className="d-flex align-items-center gap-1 mb-2 text-danger">
              <CIcon icon={cilArrowBottom} size="sm" />
              <span className="fw-semibold small text-uppercase">Самые непопулярные</span>
            </div>
            <DishTable rows={leastPopular} maxQty={maxBottomQty} color="danger" />
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
  );
}
